// Section 1: Pre-Underwriting Approval.
//
// 4 cert checkboxes the A1 signs before Section 2 unlocks:
//   UW.S1.IntakePackageReceived
//   UW.S1.BLAReadyForIssuance
//   UW.S1.AssignedTeamConfirmed
//   UW.S1.PreUnderwritingReady
//
// "Send back to B1" creates a WorkTask assigned to the deal's B1 with the
// analyst notes. Section completion is a derived state — no explicit
// "mark complete" API; Section 2 unlocks once all 4 certs are signed.

import { useState } from "react";
import { Mail } from "lucide-react";
import { Button, Card, Textarea, FormField } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { workTasksApi } from "@/api/workTasks";
import { UnderwritingCertificationCheckbox } from "../../common/UnderwritingCertificationCheckbox";
import type { DealDetailDto } from "@/types/deals";

interface PreUnderwritingApprovalSectionProps {
  deal: DealDetailDto;
}

const CERTIFICATIONS = [
  {
    code: "UW.S1.IntakePackageReceived",
    label: "I confirm the intake package is complete and accurate.",
  },
  {
    code: "UW.S1.BLAReadyForIssuance",
    label: "Broker fee authorization & Terms have been Verified.",
  },
  {
    code: "UW.S1.AssignedTeamConfirmed",
    label: "I confirm the deal team is correctly assigned for this deal.",
  },
  {
    code: "UW.S1.PreUnderwritingReady",
    label: "I attest that the deal is ready to begin formal underwriting.",
  },
] as const;

export function PreUnderwritingApprovalSection({
  deal,
}: PreUnderwritingApprovalSectionProps) {
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const toast = useToast();

  return (
    <Card>
      <header className="flex items-start justify-between gap-space-3 mb-space-3">
        <div>
          <h3 className="font-display text-h4 text-onyx m-0">
            Section 1 — Pre-Underwriting Approval
          </h3>
          <p className="text-caption text-gray-mid m-0">
            A1 confirms the intake package + BLA + team are ready before
            formal underwriting begins.
          </p>
        </div>
        <Button variant="secondary" onClick={() => setSendBackOpen(true)}>
          <Mail size={14} aria-hidden />
          Send back to B1
        </Button>
      </header>

      <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
        {CERTIFICATIONS.map((c) => (
          <li key={c.code}>
            <UnderwritingCertificationCheckbox
              dealId={deal.id}
              tab="Underwriting"
              section={1}
              certificationCode={c.code}
              label={c.label}
              {...(c.code === "UW.S1.BLAReadyForIssuance"
                ? {
                    title: "Sign certification: Broker Fee Verification",
                    attestationText:
                      "You attest that the following has been verified:\n\nBroker fee authorization & Terms have been Verified.\n\nThis confirms broker fee terms are locked for underwriting and is recorded in the audit log.",
                  }
                : {})}
            />
          </li>
        ))}
      </ul>

      <SendBackToB1Modal
        open={sendBackOpen}
        deal={deal}
        onClose={() => setSendBackOpen(false)}
        onSuccess={(msg) => {
          toast.success(msg);
          setSendBackOpen(false);
        }}
        onError={(msg) => toast.error(msg)}
      />
    </Card>
  );
}

interface SendBackToB1ModalProps {
  open: boolean;
  deal: DealDetailDto;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function SendBackToB1Modal({
  open,
  deal,
  onClose,
  onSuccess,
  onError,
}: SendBackToB1ModalProps) {
  const [notes, setNotes] = useState("");
  const [pending, setPending] = useState(false);

  const submit = async () => {
    if (!notes.trim()) return;
    setPending(true);
    try {
      await workTasksApi.create({
        title: "Pre-Approval package incomplete — see notes",
        description: notes,
        priority: "High",
        assigneeId: deal.assignedB1Id,
        relatedEntityType: "Deal",
        relatedEntityId: deal.id,
      });
      onSuccess("Sent back to B1.");
      setNotes("");
    } catch (err) {
      onError(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Could not create the task.",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Send back to B1"
      description="Creates a high-priority WorkTask assigned to the deal's B1 with your notes."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={!notes.trim() || pending}
          >
            {pending ? "Sending…" : "Send to B1"}
          </Button>
        </>
      }
    >
      <FormField label="Notes for B1" required>
        <Textarea
          rows={6}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What is missing or needs revision in the Pre-Approval package?"
        />
      </FormField>
      {!deal.assignedB1Id && (
        <p className="mt-space-2 text-caption text-status-warning">
          No B1 is assigned yet — this task will be created without an
          assignee.
        </p>
      )}
    </Modal>
  );
}
